import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';

import type { ColumnsType } from 'antd/es/table';

import { createAssetSearchFilter, mapTagsToBadgeItems } from '@/shared/lib/asset';
import { OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { openTagInNewTab } from '@/shared/lib/navigation';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { DrawerDomainList } from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import { DataTable } from '@/shared/ui/DataTable';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';

import type { Container } from '@/features/asset-detail/api/containersApi';

import styles from './ContainersTab.module.css';

type ContainersTabProps = {
  containers: Container[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onDeletedToggle: (includeDeleted: boolean) => void;
  includeDeleted: boolean;
};

type ColumnKey = 'name' | 'description' | 'owners' | 'domains' | 'tags';

const ContainersTabComponent: React.FC<ContainersTabProps> = ({
  containers,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  onDeletedToggle,
  includeDeleted,
}) => {
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const columns = useMemo<Record<ColumnKey, ColumnsType<Container>[number]>>(() => {
    const cols: Record<string, ColumnsType<Container>[number]> = {
      name: {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: 240,
        fixed: 'left',
        render: (name: string, record: Container) => (
          <Link to={`/container/${record.fullyQualifiedName}`} className={styles.containerName}>
            {record.displayName || name}
          </Link>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: containers.length > 0 ? 450 : 'auto',
        render: (description?: string) => {
          return <AssetDescription description={description} mode="expandable" />;
        },
      },
      domains: {
        title: '도메인',
        dataIndex: 'domains',
        key: 'domains',
        width: 150,
        render: (domains: Container['domains']) => {
          if (!domains || domains.length === 0) return '-';
          return <DrawerDomainList domains={domains} />;
        },
      },
      tags: {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: 250,
        render: (tags: Container['tags']) => {
          const classificationTags = filterEntityLabels(tags, {
            includeOnlySource: 'Classification',
            requireConfirmed: true,
          });
          if (classificationTags.length === 0) return '-';
          return <BadgeList items={mapTagsToBadgeItems(classificationTags)} onItemClick={openTagInNewTab} />;
        },
      },
    };

    if (showOwners) {
      cols.owners = {
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: 150,
        render: (owners: Container['owners']) => {
          if (!owners || owners.length === 0) return '-';
          return <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      };
    }

    return cols as Record<ColumnKey, ColumnsType<Container>[number]>;
  }, [showOwners, containers.length]);

  const defaultColumnOrder = useMemo<ColumnKey[]>(() => {
    const order: ColumnKey[] = ['name', 'description'];
    if (showOwners) order.push('owners');
    order.push('domains', 'tags');
    return order;
  }, [showOwners]);

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="버킷 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.containersTab}>
      <DataTable
        data={containers}
        columns={columns}
        defaultColumnOrder={defaultColumnOrder}
        defaultVisibleColumns={defaultColumnOrder}
        searchPlaceholder="버킷 검색..."
        searchFilter={createAssetSearchFilter}
        isLoading={isLoading}
        includeDeleted={includeDeleted}
        onDeletedToggle={onDeletedToggle}
        rowKey={(record) => record.id}
      />
    </div>
  );
};

export const ContainersTab = React.memo(ContainersTabComponent);
